import React, { useEffect, useRef, useState } from "react";
import { useLang } from "../context/LanguageContext";

/**
 * Premium hero — uses the real high-res Comfee Xpert AI Pro photo with
 * a cinematic 3D parallax tilt that follows the cursor (or device tilt
 * on mobile), a soft animated shadow and float bob.
 */
export default function AcCanvas3D() {
  const wrapRef = useRef(null);
  const tiltRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const [hint, setHint] = useState(true);
  const { t } = useLang();

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const onPointerMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      tiltRef.current.x = (x - 0.5) * 24;
      tiltRef.current.y = -(y - 0.5) * 16;
      setHint(false);
    };
    const onLeave = () => {
      tiltRef.current.x = 0;
      tiltRef.current.y = 0;
    };
    wrap.addEventListener("pointermove", onPointerMove);
    wrap.addEventListener("pointerleave", onLeave);

    let raf = 0;
    let mounted = true;
    let elapsed = 0;
    let last = performance.now();

    const tick = () => {
      if (!mounted) return;
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      elapsed += dt;
      tiltRef.current.tx += (tiltRef.current.x - tiltRef.current.tx) * 0.08;
      tiltRef.current.ty += (tiltRef.current.y - tiltRef.current.ty) * 0.08;
      const bob = Math.sin(elapsed * 1.3) * 6;
      const img = wrap.querySelector("[data-ac-img]");
      const shadow = wrap.querySelector("[data-ac-shadow]");
      if (img) {
        img.style.transform =
          `translateY(${bob.toFixed(2)}px) ` +
          `rotateY(${tiltRef.current.tx.toFixed(2)}deg) ` +
          `rotateX(${tiltRef.current.ty.toFixed(2)}deg)`;
      }
      if (shadow) {
        const scale = 1 - Math.abs(bob) * 0.012;
        shadow.style.transform = `translateX(-50%) scale(${scale.toFixed(3)})`;
        shadow.style.opacity = `${0.45 - Math.abs(bob) * 0.015}`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      wrap.removeEventListener("pointermove", onPointerMove);
      wrap.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="hero-3d-ac-canvas"
      className="relative w-full h-[420px] sm:h-[520px] lg:h-[600px] select-none"
      style={{ perspective: "1400px" }}
    >
      <div
        data-ac-img
        className="absolute inset-0 flex items-center justify-center will-change-transform"
        style={{ transformStyle: "preserve-3d", transition: "transform 60ms linear" }}
      >
        <img
          src="/models/ac-hero.png"
          alt="Comfee Xpert AI Pro"
          draggable={false}
          className="block w-[88%] sm:w-[78%] lg:w-[88%] mx-auto max-w-[640px]"
          style={{
            filter:
              "drop-shadow(0 18px 22px rgba(0,0,0,0.18)) drop-shadow(0 4px 8px rgba(0,0,0,0.10))",
          }}
        />
      </div>

      <div
        data-ac-shadow
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 bottom-4 w-[55%] h-6 rounded-[50%]"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.55), rgba(0,0,0,0) 70%)",
          transform: "translateX(-50%)",
          willChange: "transform, opacity",
        }}
      />

      {hint && (
        <div className="pointer-events-none absolute bottom-3 left-3 text-[10px] font-mono uppercase tracking-[0.2em] text-ink/60 hidden md:block">
          {t("ac3d.dragHint")}
        </div>
      )}
    </div>
  );
}
